<template>
    <b-container fluid class="p-3">
        <b-row class="no-gutters mb-4">
            <b-col class="d-flex flex-column">
                <span>Trust accounting can be done successfully without using this module.</span>
                <span>To learn more about how to properly set up your trust accounting processes while using Lodgix, we
                    recommend the following course at Teachable.com.</span>
                <a target='_blank' href="https://vrm-empire.teachable.com/">https://vrm-empire.teachable.com/</a>
            </b-col>
        </b-row>
        <b-row class="flex-column no-gutters align-items-start" v-if="!quickbooksData.quickbooks_linked">
            <h5 class="mb-2">Setup connection with QuickBooks Online</h5>
            <span>You must be the Master Admin for QuickBooks Online.</span>
            <ol>
                <li> Delete your Cookies and Temporary Internet Files</li>
                <li>Click the connect button below</li>
            </ol>

            <button @click="connect" class="mb-3 connect-button">
                <img style="height: 40px" src="/static/images/C2QB_white_btn_lg_default_old.png" />
            </button>
            <form-checkbox class="mb-3" v-if="quickbooksData.has_legacy_data" @change="onChangeDeleteQuickbooksData">
                Delete all existing QuickBooks sync data. <b>WARNING</b> - only check this option if you are connecting
                to
                a new QuickBooks account. This will delete all sync data existing within Lodgix and it will not be
                recoverable.
            </form-checkbox>

            <p>If you need to sign in to the Intuit App Center use QuickBooks Online User ID.</p>
        </b-row>
        <b-row class="no-gutters" v-else>
            <b-col md="6">
                <div class="d-flex justify-content-betwen align-items-center mb-4" style="gap: 20px">
                    <div class="d-flex flex-column align-items-start">
                        <h5 class="mb-2">Lodgix.com</h5>
                        <span v-for="item in lodgixItems" :key="item + 'lodgix'">
                            {{ item }}
                        </span>
                    </div>
                    <b-progress class="mt-2 progress-bar-wrapper" height="2rem">
                        <b-progress-bar :value="progress">
                            {{ progress }}%
                        </b-progress-bar>
                    </b-progress>
                    <div class="d-flex flex-column align-items-start">
                        <h5 class="mb-2">QuickBooks Online</h5>
                        <span v-for="item in lodgixItems" :key="item + 'quickbooks'">
                            {{ item }}
                        </span>
                    </div>
                </div>
                <div v-if="progress === 0" class="d-flex flex-column">
                    <p>You are now connected with QuickBooks Online. <br>Click the button below to synchronize
                        Lodgix with QuickBooks Online.
                    </p>
                    <p class="mt-2">Please note that the sync can take five minutes or more.<br>
                        PLEASE only perform the initial QuickBooks sync with your bookkeeper or accountant present.<br>
                        PLEASE only perform the initial QuickBooks sync after CAREFULLY reading the support documentation <a
                            target="_blank"
                            href="https://support.lodgix.com/en/collections/1923674-lodgix-user-manual#quickbooks-online-sync">here</a>.
                    </p>
                    <form-datepicker class="w-50 mb-4" v-if="quickbooksData.first_run" :hide-header="true"
                        :display-format="userUnicodeDateFormat" label="Initial Sync Date" v-model="firstRunDate" />
                </div>
                <p v-else>Synchronize...</p>

                <div class="d-flex">
                    <responsive-button :disabled="progress > 0" :responsiveness="false" :busy="progress > 0" class="mr-2"
                        variant="primary" @click="synchronize">
                        Synchronize
                    </responsive-button>
                    <b-button @click="$bvModal.show(disconnectModalId)" :disabled="progress > 0" variant="danger">
                        Disconnect
                    </b-button>

                </div>
            </b-col>
        </b-row>
        <modal :id="disconnectModalId" title="Disconnect" @ok.prevent="disconnect">
            Are you sure you want to disconnect from QuickBooks? This will delete all sync data.
        </modal>
    </b-container>
</template>

<script>
import api from '../../api'
import { mapGetters } from 'vuex'
import { QUICKBOOKS_ENDPOINT_OLD } from '../../endpoints'
import formCheckbox from '../generic/form-checkbox/form-checkbox.vue'
import formDatepicker from '../generic/form-datepicker/form-datepicker.vue'
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue'
import { WebSocketClient } from '../../websocket/client';
import modal from '../generic/modal/modal.vue'
import startOfYear from 'date-fns/startOfYear'
import format from 'date-fns/format'
import { formatResponseError } from '../../utils/api'

export default {
    name: 'Synchronize',
    components: { formCheckbox, formDatepicker, ResponsiveButton, modal },
    data() {
        const today = new Date();
        return {
            firstRunDate: format(startOfYear(today), 'yyyy-MM-dd'),
            lodgixItems: [
                'Accounts',
                'Classes',
                'Payment Methods',
                'Items',
                'Guests',
                'Invoices',
                'Payments'
            ],
            progress: 0,
            websocketClient: new WebSocketClient(),
            disconnectModalId: 'disconnectModalId',
            errors: 0

        }
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        ...mapGetters('quickbooksOld', ['quickbooksData']),
    },
    mounted() {
        if (window.opener) {
            window.opener.location.href = '/app/quickbooks-old/synchronize/'
            window.opener.location.reload()
            window.close()
        }
    },
    methods: {
        connect() {
            const params = `location=1,width=800,height=650,left=${(screen.width - 800) / 2},top=${(screen.height - 650) / 2}`
            window.open('/quickbooks-old/oauth/', 'connectPopup', params);
        },
        async disconnect() {
            try {
                this.$parent.$emit('busy', true)
                await api.post("/app/quickbooks-old/disconnect")
                this.$bvModal.hide(this.disconnectModalId)
                this.$store.dispatch("quickbooksOld/fetchData")
            } catch (e) {
                const { generic } = formatResponseError(e)

                this.errorToast({ text: generic || 'Could not disconnect from QuickBooks' })
            } finally {
                this.$parent.$emit('busy', false)
            }

        },
        async onChangeDeleteQuickbooksData(value) {
            try {
                await api.post(QUICKBOOKS_ENDPOINT_OLD() + 'update-delete-quickbooks-data/', {
                    checked: value
                })
            } catch (e) {
                const { generic } = formatResponseError(e)
                this.errorToast({ text: generic || 'Could not udate quickbooks data' })
            }
        },
        async synchronize() {
            try {
                this.progress = 1
                this.websocketClient.initialize({ token: this.quickbooksData.websocket_token });
                this.websocketClient.setMessageCallback(this.handleWebsocketMessage);
                this.$parent.$emit('busy', true)
                await api.post(QUICKBOOKS_ENDPOINT_OLD() + 'synchronize/', {
                    firstrun_date: this.firstRunDate,
                    token: this.quickbooksData.websocket_token
                })
                this.$store.commit('quickbooksOld/gotFirstRun', false)
            } catch (e) {
                this.progress = 0
                const { generic } = formatResponseError(e)
                this.errorToast({ text: generic || 'Could not synchronize quickbooks data' })
            } finally {
                this.$parent.$emit('busy', false)

            }
        },
        handleWebsocketMessage(message) {
            this.progress = this.getProgress(message.msg);
            const errors = this.getErrors(message.msg)
            this.errors = Number(errors)
            if (Number(errors) === -1) {
                this.errorToast({ text: 'QuickBooks sync failed with undefined error(s).' })
            } else if (Number(errors) > 0) {
                this.errorToast({ text: `QuickBooks sync failed with ${errors} error(s). Please check Sync Fail Log` })
            }
            this.errors = 0
        },
        getProgress(str) {
            const regexp = /(PROGRESS),(\d+)/i;
            const result = str && str.match(regexp);
            return (result && Number(result[2])) || this.progress;
        },
        getErrors(str) {
            const regexp = /(ENABLE_BUTTON),(\d+)/i;
            const result = str && str.match(regexp);
            return (result && Number(result[2])) || this.errors;
        }
    },

    watch: {
        progress() {
            if (this.progress === 100) {
                this.progress = 0;
                if (!this.errors) {
                    this.successToast({ text: 'Synchronization completed' })
                }
                this.websocketClient.close()
            }
        },
    }
}
</script>

<style scopedl lang="scss">
.connect-button {
    outline: none;
    border: none;
    background-color: transparent;
}

.progress-bar-wrapper {
    width: 250px;
}
</style>
